import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Package,
  MessageCircle,
  Send,
  Loader2,
  CheckCircle,
  Clock,
  Truck,
  FileText,
  Phone,
  ArrowLeft,
  Check
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  new: { label: "En attente", color: "bg-blue-500", textColor: "text-blue-600", icon: Clock },
  discussing: { label: "En discussion", color: "bg-yellow-500", textColor: "text-yellow-600", icon: MessageCircle },
  confirmed: { label: "Confirmée", color: "bg-green-500", textColor: "text-green-600", icon: CheckCircle },
  shipped: { label: "Expédiée", color: "bg-purple-500", textColor: "text-purple-600", icon: Truck },
  completed: { label: "Terminée", color: "bg-gray-500", textColor: "text-gray-600", icon: Check },
  cancelled: { label: "Annulée", color: "bg-red-500", textColor: "text-red-600", icon: Package }
};

export default function PublicOrderChat() {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [acceptingPrice, setAcceptingPrice] = useState(false);
  const messagesEndRef = useRef(null);
  
  const fetchOrder = async () => {
    if (!token) {
      setError("Token d'accès manquant");
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(`${API}/api/public/order/${orderId}?token=${token}`);
      setOrder(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError("Commande non trouvée ou lien invalide");
      } else {
        setError("Erreur lors du chargement de la commande");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrder();
    // Poll for updates every 10 seconds
    const interval = setInterval(fetchOrder, 10000);
    return () => clearInterval(interval);
  }, [orderId, token]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [order?.messages]);

  const sendMessage = async () => {
    if (!newMessage.trim() || !order) return;
    
    setSendingMessage(true);
    try {
      await axios.post(
        `${API}/api/public/order/${orderId}/messages?token=${token}`,
        { content: newMessage }
      );
      
      // Refresh order
      await fetchOrder();
      setNewMessage("");
      toast.success("Message envoyé");
    } catch (error) {
      toast.error("Erreur lors de l'envoi du message");
    } finally {
      setSendingMessage(false);
    }
  };

  const acceptPrice = async () => {
    if (!order?.proposed_price) return;
    
    setAcceptingPrice(true);
    try {
      await axios.post(
        `${API}/api/public/order/${orderId}/accept-price?token=${token}`
      );
      
      await fetchOrder();
      toast.success("Prix accepté !");
    } catch (error) {
      toast.error("Erreur lors de l'acceptation du prix");
    } finally {
      setAcceptingPrice(false);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#D4AF37] mx-auto mb-4" />
          <p className="text-[#888888]">Chargement de votre commande...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Package className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-xl font-bold text-[#1B1B1B] mb-2">Oops !</h1>
          <p className="text-[#888888]">{error}</p>
        </div>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[order?.status] || STATUS_CONFIG.new;
  const StatusIcon = statusConfig.icon;
  const hasPendingPriceProposal = order?.proposed_price && !order?.final_price && order?.status === 'discussing';

  return (
    <div className="min-h-screen bg-[#FAFAF7] flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-lg mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-bold text-[#1B1B1B]">{order?.boutique_name || order?.store_name}</h1>
              <p className="text-sm text-[#888888]">Commande {order?.order_number}</p>
            </div>
            <div className={`flex items-center gap-2 ${statusConfig.color} text-white px-3 py-1.5 rounded-full text-sm`}>
              <StatusIcon className="w-4 h-4" />
              {statusConfig.label}
            </div>
          </div>
        </div>
      </header>

      {/* Order Summary */}
      <div className="bg-white border-b border-[#F0F0F0]">
        <div className="max-w-lg mx-auto px-4 py-4">
          <p className="text-sm text-[#888888] mb-3">Commandé le {formatDate(order?.created_at)}</p>
          
          {/* Items */}
          <div className="space-y-2 mb-4">
            {order?.items.map((item, idx) => (
              <div key={idx} className="flex justify-between items-center text-sm">
                <div>
                  <span className="font-medium">{item.product_name}</span>
                  <span className="text-[#888888]"> • {item.size} • {item.color}</span>
                  {item.quantity > 1 && <span className="text-[#888888]"> × {item.quantity}</span>}
                </div>
                <span className="font-medium">{(item.unit_price * item.quantity).toFixed(2)} €</span>
              </div>
            ))}
          </div>
          
          {/* Totals */}
          <div className="border-t border-[#F0F0F0] pt-3 space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-[#888888]">Sous-total</span>
              <span>{order?.subtotal.toFixed(2)} €</span>
            </div>
            {order?.proposed_price && (
              <div className="flex justify-between text-sm text-[#D4AF37]">
                <span>Prix proposé</span>
                <span className="font-medium">{order.proposed_price.toFixed(2)} €</span>
              </div>
            )}
            {order?.final_price && (
              <div className="flex justify-between text-lg font-bold text-[#1B1B1B]">
                <span>Total</span>
                <span>{order.final_price.toFixed(2)} €</span>
              </div>
            )}
          </div>
          
          {/* Accept Price Button */}
          {hasPendingPriceProposal && (
            <div className="mt-4 p-4 bg-[#D4AF37]/10 rounded-xl">
              <p className="text-sm text-[#1B1B1B] mb-3">
                La boutique vous propose un prix de <strong>{order.proposed_price.toFixed(2)} €</strong>
              </p>
              <Button 
                onClick={acceptPrice}
                disabled={acceptingPrice}
                className="w-full bg-[#D4AF37] hover:bg-[#B8963C] text-white"
              >
                {acceptingPrice ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Accepter ce prix
              </Button>
            </div>
          )}
          
          {/* Invoice Link */}
          {order?.invoice_id && (
            <Button 
              variant="outline"
              className="w-full mt-4 border-[#D4AF37] text-[#D4AF37]"
              onClick={() => window.open(`/invoice/${order.invoice_id}`, '_blank')}
            >
              <FileText className="w-4 h-4 mr-2" />
              Voir ma facture
            </Button>
          )}
        </div>
      </div>

      {/* Chat Messages */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-lg mx-auto px-4 py-4 space-y-3">
          {order?.messages.length === 0 ? (
            <div className="text-center py-8">
              <MessageCircle className="w-12 h-12 mx-auto mb-3 text-[#E5E5E5]" />
              <p className="text-[#888888]">Aucun message pour le moment</p>
              <p className="text-sm text-[#AAAAAA] mt-1">La boutique vous répondra bientôt</p>
            </div>
          ) : (
            order.messages.map(msg => (
              <div 
                key={msg.id}
                className={`flex ${msg.sender === 'customer' ? 'justify-end' : msg.sender === 'system' ? 'justify-center' : 'justify-start'}`}
              >
                {msg.sender === 'system' ? (
                  <div className="bg-[#F5F5F5] text-[#888888] text-xs px-4 py-2 rounded-full">
                    {msg.content}
                  </div>
                ) : (
                  <div className={`max-w-[80%] ${
                    msg.sender === 'customer' 
                      ? 'bg-[#1B1B1B] text-white rounded-tl-2xl rounded-tr-2xl rounded-bl-2xl' 
                      : 'bg-white border border-[#F0F0F0] rounded-tl-2xl rounded-tr-2xl rounded-br-2xl shadow-sm'
                  } px-4 py-3`}>
                    {msg.sender === 'store' && (
                      <p className="text-xs text-[#D4AF37] font-medium mb-1">{order.boutique_name || order.store_name}</p>
                    )}
                    <p className={`text-sm whitespace-pre-wrap ${msg.sender === 'customer' ? 'text-white' : 'text-[#1B1B1B]'}`}>
                      {msg.content}
                    </p>
                    {msg.image_url && (
                      <img src={msg.image_url} alt="Image" className="mt-2 rounded-lg max-w-full" />
                    )}
                    <p className={`text-xs mt-2 text-right ${msg.sender === 'customer' ? 'text-white/60' : 'text-[#AAAAAA]'}`}>
                      {formatTime(msg.created_at)}
                    </p>
                  </div>
                )}
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Message Input */}
      <div className="bg-white border-t border-[#F0F0F0] sticky bottom-0">
        <div className="max-w-lg mx-auto px-4 py-3">
          <div className="flex gap-2">
            <Input
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Écrivez votre message..."
              onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
              className="flex-1 bg-[#F5F5F5] border-[#E5E5E5] rounded-full px-4"
            />
            <Button 
              onClick={sendMessage}
              disabled={!newMessage.trim() || sendingMessage}
              className="bg-[#1B1B1B] hover:bg-[#333] rounded-full w-12 h-12 p-0"
            >
              {sendingMessage ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Send className="w-5 h-5" />
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
